import ErrorFallback from './ErrorFallback';
import RoutesComponent from './routes/RoutesComponent';
import Toast from './components/Toast';
import CustomModal from './components/CustomModal';
import store from './rematch';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import './assets/scss/_global.scss';
import { useEffect, useRef, useState } from 'react';
import { cookieDecryption, getAccessToken } from './utils';
import { resolveNotificationIdentity } from './utils/notificationCountIdentity';
import TagManager from 'react-gtm-module';
import CookiePopup from './components/Popup/CookiePopup';
import SessionTimeoutModal from './components/Popup/SessionTimeoutModal';
import axiosClient from './api/axiosClient';
import InactivityInfoModal from './components/Popup/InactivityInfoModal';
import { applyNewRelicUserContext } from './observability/applyNewRelicUserContext';
import { safeWindowLocation } from './utils/safeNavigation';
import { fetchUnreadCountWithRetry } from './utils/fetchUnreadCountWithRetry';

/** Retries allowed on the once-per-session bell count fetch before it gives up. */
const UNREAD_COUNT_RETRIES = 2;

/** Gap between those retries, long enough to outlast a brief network blip. */
const UNREAD_COUNT_RETRY_DELAY_MS = 5000;

const App = () => {
  const auth = cookieDecryption('userDetails');

  window.addEventListener('storage', function (e) {
    if (e.key === 'userStatus') {
      window.location.reload();
    }
  });

  const userData = useSelector(state => state.authModel.logInUser);
  const notificationSessionPending = useSelector(state => state.authModel.notificationSessionPending);
  const notificationIdentity = resolveNotificationIdentity(auth, userData);
  const socket = useSelector(state => state?.socketModel?.socket);
  const socketConnected = useSelector(state => state?.socketModel?.socketConnected);
  const toaster = useSelector(state => state.toastModel);
  const dialogModel = useSelector(state => state.dialogModel);
  const { open, component: Component, className } = dialogModel;
  const { dispatch } = store;

  // Inactivity timer state
  const [idleSeconds, setIdleSeconds] = useState(0);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(30);
  const [showInactivityInfo, setShowInactivityInfo] = useState(false);
  const unreadCountFetchedForRef = useRef(null);

  useEffect(() => {
    if (Object.keys(userData || {}).length && !socketConnected) {
      dispatch.socketModel.getSocket({ token: userData?.token, userData });
    }
  }, [userData]);

  useEffect(() => {
    applyNewRelicUserContext(userData);
  }, [userData]);

  useEffect(() => {
    if (!Object.keys(socket || {}).length) return;

    const handleMessageSent = eventPayload => {
      dispatch.socketModel.handleMessageSentEvent(eventPayload);
    };

    const handleMessageReactionSent = eventPayload => {
      dispatch.socketModel.handleMessageReactionSentEvent(eventPayload);
    };

    const handleChatMessagesMarkedAsRead = response => {
      dispatch.socketModel.handleChatMessagesMarkedAsReadEvent(response);
    };

    const handleResponse = response => {
      dispatch.socketModel.socketResponse({ response });
    };

    const handleStatusUpdate = eventPayload => {
      dispatch.socketModel.handleMessageStatusUpdate(eventPayload);
    };

    socket.on('response', handleResponse);
    socket.on('messageReactionSent', handleMessageReactionSent);
    socket.on('messageSent', handleMessageSent);
    socket.on('chatMessagesMarkedAsRead', handleChatMessagesMarkedAsRead);
    socket.on('messageStatusUpdate', handleStatusUpdate);

    return () => {
      socket.off('response', handleResponse);
      socket.off('messageReactionSent', handleMessageReactionSent);
      socket.off('messageSent', handleMessageSent);
      socket.off('chatMessagesMarkedAsRead', handleChatMessagesMarkedAsRead);
      socket.off('messageStatusUpdate', handleStatusUpdate);
    };

    // if (Object.keys(socket || {}).length) {
    //   socket.on('response', response => {
    //     dispatch.socketModel.socketResponse({ response });
    //   });
    // }
    // return () => {
    //   if (Object.keys(socket || {}).length) {
    //     socket.off('response', function (err) {
    //       console.error('response off', err);
    //     });
    //   }
    // };
  }, [socket]);

  const tagManagerArgs = {
    gtmId: 'GTM-PFF836CC',
  };

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
  }, []);
  useEffect(() => {
    if (auth) {
      let schools =
        typeof auth?.assignedSchoolOrInstitutions === 'string'
          ? [auth?.assignedSchoolOrInstitutions]
          : auth?.assignedSchoolOrInstitutions;
      const tagManagerArgs = {
        gtmId: 'GTM-PFF836CC',
        dataLayer: {
          user_type: auth?.role?.toLowerCase(),
          user_org: auth?.partnerAdmin?.partnerName || auth?.region?.region || '',
          user_school: schools?.join(',').toLowerCase() || '',
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  }, [auth]);

  useEffect(() => {
    if (auth && !notificationSessionPending) {
      dispatch.authModel.getUserDetails({});
    }
  }, [notificationSessionPending]);

  /**
   * Fetch the bell count once per signed-in session.
   *
   * A failed attempt is retried rather than counted as the one this session
   * gets. There is no other way back: the socket pushes a count when a
   * notification arrives, not when it connects, so a blip at startup would
   * otherwise leave the badge reading zero over unread items for the whole
   * visit. After the last retry the session is marked unfetched again, so a
   * later run of this effect starts over rather than believing a count was
   * read that never was.
   */
  useEffect(() => {
    if (notificationSessionPending) {
      return undefined;
    }

    if (!notificationIdentity) {
      unreadCountFetchedForRef.current = null;

      return undefined;
    }

    if (unreadCountFetchedForRef.current === notificationIdentity) {
      return undefined;
    }

    unreadCountFetchedForRef.current = notificationIdentity;

    return fetchUnreadCountWithRetry({
      getUnreadCount: () => dispatch.notificationsModel.getUnreadCount(),
      retries: UNREAD_COUNT_RETRIES,
      delayMs: UNREAD_COUNT_RETRY_DELAY_MS,
      onGiveUp: () => {
        unreadCountFetchedForRef.current = null;
      },
    });
  }, [notificationIdentity, notificationSessionPending]);

  // Show homepage modal if redirected after inactivity logout
  useEffect(() => {
    const reason = localStorage.getItem('logoutReason');
    if (reason === 'inactivity') {
      setShowInactivityInfo(true);
      localStorage.removeItem('logoutReason');
    }
  }, []);

  // Format countdown display
  const formatCountdown = seconds => {
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins} minute${mins > 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  };

  // Inactivity tracking: 5min idle -> show modal; do not auto-dismiss on movement
  useEffect(() => {
    let idleInterval;
    const IDLE_THRESHOLD = 5 * 60; // 5 minutes till show modal

    const userActivity = () => {
      if (!showTimeoutModal) {
        setIdleSeconds(0);
      }
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, userActivity, { passive: true }));

    idleInterval = setInterval(() => {
      setIdleSeconds(prev => {
        const next = prev + 1;
        const cookieAuth = cookieDecryption('userDetails') || {};

        const flag =
          userData && userData.sessionTimeoutNeeded !== undefined
            ? userData.sessionTimeoutNeeded
            : cookieAuth.sessionTimeoutNeeded;
        const needsTimeout = flag === true || flag === 'true';

        if (next >= IDLE_THRESHOLD && getAccessToken() && needsTimeout) {
          setShowTimeoutModal(true);
        }
        return next;
      });
    }, 1000);

    return () => {
      clearInterval(idleInterval);
      events.forEach(e => window.removeEventListener(e, userActivity));
    };
  }, [showTimeoutModal, userData]);

  // Modal countdown: 2min then auto-logout (SLO if available)
  useEffect(() => {
    if (!showTimeoutModal) return;
    setCountdownSeconds(2 * 60); // 2 minutes till auto-logout
    const id = setInterval(() => {
      setCountdownSeconds(cs => {
        if (cs <= 1) {
          clearInterval(id);
          axiosClient
            .get('auth/log-out')
            .then(res => {
              // Clear local auth footprint before redirect to avoid unauthorized flash
              document.cookie = 'userDetails=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
              localStorage.setItem('userStatus', 'logged Out');
              localStorage.setItem('logoutReason', 'inactivity');
              const sloUrl = res?.data?.data?.sloLogoutUrl;
              // EC-37: validate API-provided SLO URL before navigation.
              // BugBot PR-229: fallback to '/' when sloUrl is present but fails
              // validation, so the user is always redirected after logout.
              const navigated = sloUrl && safeWindowLocation(sloUrl, 'replace');
              if (!navigated) {
                window.location.replace('/');
              }
            })
            .catch(() => {
              document.cookie = 'userDetails=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
              localStorage.setItem('userStatus', 'logged Out');
              localStorage.setItem('logoutReason', 'inactivity');
              window.location.replace('/');
            });
        }
        return cs - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [showTimeoutModal]);

  const onClose = () => {
    dispatch.dialogModel.hideDialog({});
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <RoutesComponent />
      <Toast toastList={toaster.list} autoDelete={true} autoDeleteTime={3000} />
      <CustomModal className={className} onClose={onClose} open={open}>
        <Component />
      </CustomModal>
      {showTimeoutModal && (
        <CustomModal open={true} onClose={() => setShowTimeoutModal(false)} className="!h-auto">
          <SessionTimeoutModal
            secondsRemaining={formatCountdown(countdownSeconds)}
            onLogout={() => {
              setShowTimeoutModal(false);
            }}
          />
        </CustomModal>
      )}
      {showInactivityInfo && (
        <CustomModal open={true} onClose={() => setShowInactivityInfo(false)} className="!h-auto">
          <InactivityInfoModal onClose={() => setShowInactivityInfo(false)} />
        </CustomModal>
      )}
      <CookiePopup />
    </ErrorBoundary>
  );
};

export default App;
